import {
  Address,
  DraftEmailType,
  EmailProvider,
  EmailRequestType,
} from '@this+that/models';

import type { DraftAccount } from './resolveEmailAccount';
import type { EmailSendRequest } from '../../../services/unifiedEmailAction.service';
import {
  EMPTY_CONTENT_PATTERNS,
  extractUserInputFromTemplate,
  hasTemplateContent,
  isContentEmpty,
} from '../../../utils/editorContentUtils';
import { getAddressesByType } from '../../../utils/getAddressesByType';
import type { FileAttachment } from '../../index';

export interface DraftRequestContent {
  content?: string;
  subject?: string;
  recipients?: Address[];
  attachments?: FileAttachment[];
  draftId?: string;
  messageId?: string;
  draftType?: DraftEmailType;
  modifiedQuotedContent?: string;
  expectedDraftVersion?: string;
  attachmentsToRemove?: string[];
}

const resolveBodyContent = (
  content: string | undefined,
  draftType: DraftEmailType | undefined,
): string | undefined =>
  content &&
  hasTemplateContent(content) &&
  (draftType === DraftEmailType.REPLY || draftType === DraftEmailType.FORWARD)
    ? extractUserInputFromTemplate(content)
    : content;

const resolveRequestBody = (
  bodyContent: string | undefined,
  isUpdatingExistingDraft: boolean,
): string | undefined => {
  if (bodyContent && !isContentEmpty(bodyContent)) {
    return bodyContent;
  }
  // server treats a falsy body as "keep existing draft body", so send a visually-blank non-empty body to actually clear it
  return isUpdatingExistingDraft
    ? EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR
    : undefined;
};

export const buildDraftRequest = (
  {
    content,
    subject,
    recipients = [],
    attachments,
    draftId,
    messageId,
    draftType,
    modifiedQuotedContent,
    expectedDraftVersion,
    attachmentsToRemove,
  }: DraftRequestContent,
  { accountId, platform }: DraftAccount,
): EmailSendRequest => {
  const {
    to: toAddresses = [],
    cc: ccAddresses = [],
    bcc: bccAddresses = [],
  } = getAddressesByType(recipients);

  return {
    providerAccount: { provider: platform as EmailProvider, accountId },
    reqType: EmailRequestType.DRAFT,
    body: resolveRequestBody(
      resolveBodyContent(content, draftType),
      Boolean(draftId),
    ),
    subject: subject === '' ? undefined : subject,
    // The compositor is the single owner of the reviewed recipient lists here — an
    // empty array must reach the server as "clear this field", not be collapsed to
    // undefined ("keep whatever the draft already has").
    toAddresses,
    ccAddresses,
    bccAddresses,
    attachments: attachments?.length ? attachments : undefined,
    draftId,
    messageId,
    draftType,
    modifiedQuotedContent,
    expectedDraftVersion,
    attachmentsToRemove,
  };
};
